import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { ShareModal } from '../components/organisms/ShareModal';
import { usePortal } from '../../../providers';
import { useCreateShare } from './useCreateShare';
import { useDeleteShare } from './useDeleteShare';

const useShare = (activeUsers, participants, shareableEntity, currentUser) => {
  const sortedParticipants = useMemo(() => {
    const supperUsers = participants.filter((user) => user.email?.endsWith('@supper.co'));
    const otherUsers = participants.filter((user) => !user.email?.endsWith('@supper.co'));
    const spaceCreator = activeUsers.find((user) => user.id === shareableEntity?.userId);

    if (spaceCreator) {
      if (spaceCreator?.email?.endsWith('@supper.co')) {
        supperUsers.push(spaceCreator);
      } else {
        otherUsers.push(spaceCreator);
      }
    }

    if (currentUser) {
      if (currentUser?.email?.endsWith('@supper.co')) {
        supperUsers.push(currentUser);
      } else {
        otherUsers.push(currentUser);
      }
    }

    return [
      otherUsers.sort((a, b) => a.name.localeCompare(b.name)),
      supperUsers.sort((a, b) => a.name.localeCompare(b.name))
    ];
  }, [participants, activeUsers, shareableEntity?.userId, currentUser?.id]);

  const sortedActiveUsers = useMemo(() => {
    const supperUsers = activeUsers.filter((user) => user.email?.endsWith('@supper.co'));
    const otherUsers = activeUsers.filter((user) => !user.email?.endsWith('@supper.co'));

    return [
      ...otherUsers.sort((a, b) => a.name.localeCompare(b.name)),
      ...supperUsers.sort((a, b) => a.name.localeCompare(b.name))
    ];
  }, [activeUsers]);

  // Destructure portal and showPortal from the PortalProvider
  const { portal, showPortal } = usePortal();
  const createShare = useCreateShare();
  // Save the modal parameters so we can update the portal content when props change.
  const [modalProps, setModalProps] = useState(null);

  const handleShare = useCallback(
    (users, shareableId, type = 'question') => {
      if (users.length > 0) {
        createShare(
          {
            participants: [...users.map((item) => [item.id, 'view'])],
            shareable_id: shareableId,
            shareable_type: type
          },
          'Share',
          users.map((item) => item.name).join(', ')
        );
      }
    },
    [createShare]
  );

  // onShare saves the props and opens the portal with the initial modal
  const onShare = useCallback(
    (itemId, type, schema = null) => {
      const newModalProps = { itemId, type, schema };
      setModalProps(newModalProps);
      showPortal(
        <ShareModal
          schema={schema}
          title={`Invite people to join the ${type}`}
          shareableId={itemId}
          label='Choose who you want to invite'
          buttonLabel='Invite'
          currentUser={currentUser}
          onCreateShare={handleShare}
          users={sortedActiveUsers}
          organizationParticipants={sortedParticipants[0]}
          supperParticipants={sortedParticipants[1]}
          type={type}
          shareableEntity={shareableEntity}
        />
      );
    },
    [sortedActiveUsers, sortedParticipants, shareableEntity, showPortal]
  );

  // When participants (or other related props) change and the modal is open,
  // update the portal content with the latest props.
  useEffect(() => {
    if (portal && modalProps) {
      showPortal(
        <ShareModal
          schema={modalProps.schema}
          title={`Invite people to join the ${modalProps.type}`}
          shareableId={modalProps.itemId}
          label='Choose who you want to invite'
          buttonLabel='Invite'
          currentUser={currentUser}
          onCreateShare={handleShare}
          users={sortedActiveUsers}
          organizationParticipants={sortedParticipants[0]}
          supperParticipants={sortedParticipants[1]}
          type={modalProps.type}
          shareableEntity={shareableEntity}
        />
      );
    }
  }, [sortedParticipants, sortedActiveUsers, shareableEntity, modalProps]);

  const onDeleteShare = useDeleteShare();

  return {
    onShare,
    onDeleteShare
  };
};

export { useShare };
